import React from 'react';
import { DateTime, Passenger } from 'acscp/store';
import { FormattedMessage } from 'react-intl';
import style from './passengerDetails.module.scss';
import { FormattedDate, FormattedDateWithMaskedDay, yearDateMaskSymbol, yearDateMaskValue } from 'acscp/components/common/formattedDate';
import { LabeledItem } from 'acscp/components/common/labeledItem';
import { withCollapsable } from 'acscp/components/common/collapsable';
import { Title } from 'acscp/components/itinerary/collapsableSections';
import { VisaMessage } from 'acscp/components/itinerary/visaMessage';

const MobilePassengerDetails: React.SFC<{ passengers: Passenger[] }> = ({ passengers }) => {
    return (
        <div className={style.MobilePassengers}>
            {passengers.map((passenger, index) => (
                <div key={index} className={style.ItemContainerBordered}>
                    {passengers.length > 1 && <div className={style.MobilePassengerOrder}>{passenger.order + 1}</div>}
                    <LabeledItem
                        label={
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.name'}
                                defaultMessage={'Name'}
                            />}
                    >
                        {passenger.name}
                    </LabeledItem>
                    <LabeledItem
                        label={
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.nationality'}
                                defaultMessage={'Nationality'}
                            />}
                    >
                        {passenger.nationality}
                    </LabeledItem>
                    <LabeledItem
                        label={
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedDateOfBirth'}
                                defaultMessage={'DOB'}
                            />}
                    >
                        {!!passenger.maskedDateOfBirth &&
                            (
                                <FormattedDate
                                    date={new DateTime(
                                        passenger.maskedDateOfBirth.replace(yearDateMaskSymbol, yearDateMaskValue))}
                                    maskYear={true}
                                />
                            )}
                    </LabeledItem>
                    <LabeledItem
                        label={
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedPassportNumber'}
                                defaultMessage={'Passport No.'}
                            />}
                    >
                        {passenger.maskedPassportNumber}
                    </LabeledItem>
                    <LabeledItem
                        label={
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedPassportExpiryDate'}
                                defaultMessage={'Passport Expiry'}
                            />}
                    >
                        {!!passenger.maskedPassportExpiryDate &&
                            (
                                <FormattedDateWithMaskedDay
                                    maskedApiDate={passenger.maskedPassportExpiryDate}
                                />
                            )}
                    </LabeledItem>
                </div>
            ))}
        </div>
    );
};

const LargePassengerDetails: React.SFC<{ passengers: Passenger[] }> =
    ({ passengers}) => {
    passengers.sort((a, b) => (a.order - b.order));
    const showNationalityColumn = passengers.some(passenger => passenger.nationality);
    const showDateOfBirthColumn = passengers.some(passenger => passenger.maskedDateOfBirth);
    const showPassportNumberColumn = passengers.some(passenger => passenger.maskedPassportNumber);
    const showPassportExpiryDateColumn = passengers.some(passenger => passenger.maskedPassportExpiryDate);
    return (
        <table className={style.PassengersTable}>
            <thead>
                <tr>
                    <th className={style.rowIndex}/>
                    <th>
                        <FormattedMessage id={'web.components.itinerary.passengers.name'} defaultMessage={'Name'}/>
                    </th>
                    {showNationalityColumn &&
                        <th>
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.nationality'}
                                defaultMessage={'Nationality'}
                            />
                        </th>
                    }
                    {showDateOfBirthColumn &&
                        <th>
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedDateOfBirth'}
                                defaultMessage={'DOB'}
                            />
                        </th>
                    }
                    {showPassportNumberColumn &&
                        <th>
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedPassportNumber'}
                                defaultMessage={'Passport No.'}
                            />
                        </th>
                    }
                    {showPassportExpiryDateColumn &&
                        <th>
                            <FormattedMessage
                                id={'web.components.itinerary.passengers.maskedPassportExpiryDate'}
                                defaultMessage={'Passport Expiry'}
                            />
                        </th>
                    }
                </tr>
            </thead>
            <tbody>
                {passengers.map((passenger, index) => (
                    <tr key={index}>
                        <td className={style.rowIndex}>
                            {passenger.order + 1}.
                        </td>
                        <td>
                            {passenger.name}
                        </td>
                        {showNationalityColumn &&
                            <td>
                                {passenger.nationality}
                            </td>
                        }
                        {showDateOfBirthColumn &&
                            <td>
                                {!!passenger.maskedDateOfBirth &&
                                    (
                                        <FormattedDate
                                            date={new DateTime(
                                                passenger.maskedDateOfBirth.replace(yearDateMaskSymbol, yearDateMaskValue))}
                                            maskYear={true}
                                        />
                                    )}
                            </td>
                        }
                        {showPassportNumberColumn &&
                            <td>
                                {passenger.maskedPassportNumber}
                            </td>
                        }
                        {showPassportExpiryDateColumn &&
                            <td>
                                {!!passenger.maskedPassportExpiryDate &&
                                    (
                                        <FormattedDateWithMaskedDay
                                            maskedApiDate={passenger.maskedPassportExpiryDate}
                                        />
                                    )}
                            </td>
                        }
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const PassengerDetailsContent: React.SFC<{ passengers: Passenger[]}> =
    ({passengers}) => {
    return (
        <div className={style.Container}>
            <VisaMessage />
            {passengers.length !== 0 &&
                <MobilePassengerDetails passengers={passengers} />
            }
            {passengers.length !== 0 &&
                <LargePassengerDetails passengers={passengers} />
            }
        </div>
    );
};

const PassengerDetails = withCollapsable(
    (
        <Title>
            <FormattedMessage id={'web.components.itinerary.passengerDetailsTitle'} defaultMessage={'PASSENGER DETAILS'} />
        </Title>
    ),
    PassengerDetailsContent);
export { PassengerDetails };
